// Auth state. Holds tokens + customer/user profile. Persisted to localStorage
// so reload doesn't drop the session. The api/client interceptor reads the
// token from this store and calls refreshSession on 401.
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AUTH_STORAGE_KEY, REFRESH_THRESHOLD_MS } from '@core/constants/auth';
import { refreshTokenRequest } from '@core/api/refresh';

/** @typedef {import('@core/types').AuthSession} AuthSession */
/** @typedef {import('@core/types').Customer} Customer */

const empty = {
  accessToken: null,
  refreshToken: null,
  tokenType: 'Bearer',
  expiresAt: 0,
  accountId: null,
  customer: /** @type {Customer | null} */ (null),
};

function sessionPayload(resp) {
  if (resp?.access_token || resp?.customer || resp?.user) return resp;
  if (resp?.data && typeof resp.data === 'object') return resp.data;
  return resp;
}

// The Bytz API field is named `expires_in` but its value is the *absolute*
// Unix-seconds expiry (matches the JWT exp claim) rather than a relative
// duration. Detect either shape so we work against any backend that swings
// the field name's intent. Threshold ~year 2001 — anything bigger has to
// be an absolute timestamp.
function computeExpiresAt(expiresIn) {
  if (!expiresIn || expiresIn <= 0) return 0;
  if (expiresIn > 1e9) return expiresIn * 1000;
  return Date.now() + expiresIn * 1000;
}

// /auth/login returns `user`, /customers/login returns `customer`. Normalise.
function normaliseProfile(raw, prevCustomer) {
  const src = raw?.customer ?? raw?.user;
  if (!src) return prevCustomer;
  return {
    customer_id: src.customer_id ?? src.user_id ?? prevCustomer?.customer_id,
    email: src.email ?? prevCustomer?.email,
    first_name: src.first_name ?? prevCustomer?.first_name ?? '',
    last_name: src.last_name ?? prevCustomer?.last_name,
    mobile: src.mobile ?? prevCustomer?.mobile,
    role_id: src.role_id ?? prevCustomer?.role_id,
  };
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      ...empty,

      /** @returns {boolean} */
      isAuthenticated: () => Boolean(get().accessToken && get().customer),

      /** @returns {boolean} */
      isExpiringSoon: () => {
        const { expiresAt } = get();
        return expiresAt > 0 && expiresAt - Date.now() < REFRESH_THRESHOLD_MS;
      },

      /**
       * Apply a fresh login/refresh response. Tolerant of both
       *   { ..., customer: { customer_id, ... } }   (storefront /customers/login)
       *   { ..., user:     { user_id,     ... } }   (admin /auth/login)
       * @param {object} resp
       */
      setSession: (resp) => {
        const payload = sessionPayload(resp);
        set({
          accessToken: payload?.access_token ?? null,
          refreshToken: payload?.refresh_token ?? null,
          tokenType: payload?.token_type || 'Bearer',
          expiresAt: computeExpiresAt(payload?.expires_in),
          accountId: payload?.account_id ?? get().accountId,
          customer: normaliseProfile(payload, get().customer),
        });
      },

      /** @param {Customer} customer */
      setCustomer: (customer) => set({ customer }),

      clearSession: () => set({ ...empty }),

      /**
       * Exchange the persisted refresh token for a new access token.
       * Throws on failure — the api client interceptor catches and clears.
       */
      refreshSession: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          throw new Error('No refresh token');
        }
        const resp = await refreshTokenRequest(refreshToken);
        const payload = sessionPayload(resp);
        set({
          accessToken: payload?.access_token ?? get().accessToken,
          refreshToken: payload?.refresh_token ?? refreshToken,
          tokenType: payload?.token_type || get().tokenType || 'Bearer',
          expiresAt: computeExpiresAt(payload?.expires_in) || get().expiresAt,
        });
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        tokenType: s.tokenType,
        expiresAt: s.expiresAt,
        accountId: s.accountId,
        customer: s.customer,
      }),
    },
  ),
);
